//DashboardLayout.jsx
import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import {
  HomeIcon,
  FlagIcon,
  ClockIcon,
  WalletIcon,
  UserIcon,
  ChatBubbleLeftRightIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  MoonIcon,
  SunIcon,
  UsersIcon,
  BookOpenIcon,
  ArchiveBoxIcon,
  GiftIcon,
  TruckIcon,
  UserPlusIcon,
  CreditCardIcon,
  PaperAirplaneIcon,
} from "@heroicons/react/24/outline";
import Currency from "../components/Currency";
import { useWallet } from "../context/WalletContext";

const WhatsAppIcon = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

export default function DashboardLayout() {
  const navigate = useNavigate();
  const { walletBalance } = useWallet();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarMounted, setSidebarMounted] = useState(false);
  const [darkMode, setDarkMode] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem("theme");
    const prefersDark = window.matchMedia(
      "(prefers-color-scheme: dark)"
    ).matches;
    const enabled = saved ? saved === "dark" : prefersDark;
    setDarkMode(enabled);
    document.documentElement.classList.toggle("dark", enabled);
  }, []);

  const toggleTheme = () => {
    const next = !darkMode;
    setDarkMode(next);
    document.documentElement.classList.toggle("dark", next);
    localStorage.setItem("theme", next ? "dark" : "light");
  };

  useEffect(() => {
    if (!localStorage.getItem("accessToken")) navigate("/login");
  }, [navigate]);

  const handleLogout = () => {
    localStorage.clear();
    navigate("/login");
  };

  const openSidebar = () => {
    setSidebarOpen(true);
    requestAnimationFrame(() => setSidebarMounted(true));
  };
  const closeSidebar = () => {
    setSidebarMounted(false);
    setTimeout(() => setSidebarOpen(false), 250);
  };

  const navItems = [
  { to: "/dashboard", label: "Dashboard", icon: HomeIcon },
  { to: "/dashboard/referrals", label: "Refer & Earn", icon: UserPlusIcon, badge: "₦200" },
  { to: "/dashboard/usa-numbers", label: "Buy Numbers", icon: FlagIcon },
  { to: "/dashboard/tutorials", label: "Tutorials", icon: BookOpenIcon },
  { to: "/dashboard/social-logs", label: "Social Logs", icon: UsersIcon },
  { to: "/dashboard/gifts", label: "Send Gifts", icon: GiftIcon },
  { to: "/dashboard/gift-cards", label: "Gift Cards", icon: CreditCardIcon },
  { to: "/dashboard/gift-orders", label: "Gift Orders", icon: TruckIcon },
  { to: "/dashboard/orders", label: "My Orders", icon: ArchiveBoxIcon },
  { to: "/dashboard/history", label: "History", icon: ClockIcon },
    { to: "/dashboard/wallet", label: "Wallet", icon: WalletIcon },
    { to: "/dashboard/transfer", label: "Transfer", icon: PaperAirplaneIcon },
    { to: "/dashboard/profile", label: "Profile", icon: UserIcon },
    {
      to: "https://wa.me/2348079208230",
      label: "Contact Us",
      icon: ChatBubbleLeftRightIcon,
      external: true,
    },
  ];

  const bottomNavItems = [
    { to: "/dashboard", label: "Home", icon: HomeIcon },
    { to: "/dashboard/usa-numbers", label: "Numbers", icon: FlagIcon },
    { to: "/dashboard/social-logs", label: "Logs", icon: UsersIcon },
    { to: "/dashboard/gifts", label: "Gifts", icon: GiftIcon },
    { to: "/dashboard/profile", label: "Profile", icon: UserIcon },
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <header className="hidden md:block bg-white/90 dark:bg-gray-900/90 backdrop-blur-md border-b border-gray-200 dark:border-gray-800 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6">
          <div className="h-16 flex items-center gap-10">
            <Link to="/" className="flex items-center gap-2.5 shrink-0">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-600 to-purple-800 text-white font-bold flex items-center justify-center shadow-sm text-[13px]">
                LD
              </div>
              <span className="font-semibold text-gray-900 dark:text-white">
                LarryDigitals
              </span>
            </Link>

            <nav className="flex items-center gap-1 overflow-x-auto">
              {navItems.map(({ to, label, icon: Icon, external, badge, comingSoon }) => {
                if (comingSoon) {
                  return (
                    <div
                      key={label}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap text-gray-400 dark:text-gray-600 cursor-not-allowed select-none"
                    >
                      <Icon className="w-4 h-4" />
                      {label}
                      <span className="ml-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 leading-none uppercase">
                        Soon
                      </span>
                    </div>
                  );
                }
                return (
                  <NavLink
                    key={label}
                    to={to}
                    end={to === "/dashboard"}
                    target={external ? "_blank" : undefined}
                    className={({ isActive }) =>
                      `flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-150 whitespace-nowrap ${
                        isActive
                          ? "bg-purple-600 text-white shadow-sm"
                          : "text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
                      }`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <Icon className="w-4 h-4" />
                        {label}
                        {badge && !isActive && (
                          <span className="ml-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-amber-400 text-amber-900 leading-none">
                            {badge}
                          </span>
                        )}
                      </>
                    )}
                  </NavLink>
                );
              })}
            </nav>

            <div className="ml-auto flex items-center gap-3">
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-purple-50 dark:bg-purple-900/30">
                <WalletIcon className="w-4 h-4 text-purple-600 dark:text-purple-300 flex-shrink-0" />
                <div className="leading-tight">
                  <p className="text-[9px] font-medium text-purple-500 dark:text-purple-400 uppercase tracking-wide">
                    Wallet Balance
                  </p>
                  <p className="text-sm font-semibold text-purple-700 dark:text-purple-300">
                    <Currency amount={walletBalance} />
                  </p>
                </div>
              </div>

              <button
                onClick={toggleTheme}
                className="p-2 rounded-full border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 transition-transform duration-200 active:scale-90"
                aria-label="Toggle theme"
              >
                {darkMode ? (
                  <SunIcon className="w-5 h-5 text-amber-300" />
                ) : (
                  <MoonIcon className="w-5 h-5 text-gray-600" />
                )}
              </button>

              <button
                onClick={handleLogout}
                className="flex items-center gap-1.5 text-sm font-medium text-red-500 border border-red-300 dark:border-red-800 px-3 py-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
              >
                <ArrowRightOnRectangleIcon className="w-4 h-4" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <header className="md:hidden bg-white/90 dark:bg-gray-900/90 backdrop-blur-md border-b border-gray-200 dark:border-gray-800 sticky top-0 z-40">
        <div className="h-14 px-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-purple-600 to-purple-800 text-white font-bold flex items-center justify-center text-[11px]">
              LD
            </div>
            <span className="font-semibold text-gray-900 dark:text-white text-sm">
              LarryDigitals
            </span>
          </Link>

          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-purple-50 dark:bg-purple-900/30">
              <WalletIcon className="w-3.5 h-3.5 text-purple-600 dark:text-purple-300 flex-shrink-0" />
              <div className="leading-none">
                <p className="text-[11px] font-semibold text-purple-700 dark:text-purple-300">
                  <Currency amount={walletBalance} />
                </p>
              </div>
            </div>
            <button onClick={openSidebar} aria-label="Open menu">
              <Bars3Icon className="w-6 h-6 text-gray-900 dark:text-white" />
            </button>
          </div>
        </div>
      </header>

      {sidebarOpen && (
        <>
          <div
            className={`fixed inset-0 bg-black/50 z-40 md:hidden transition-opacity duration-250 ${
              sidebarMounted ? "opacity-100" : "opacity-0"
            }`}
            onClick={closeSidebar}
          />
          <aside
            className={`fixed top-0 left-0 h-full w-72 bg-white dark:bg-gray-900 z-50 p-5 md:hidden overflow-y-auto flex flex-col shadow-2xl transition-transform duration-250 ease-out ${
              sidebarMounted ? "translate-x-0" : "-translate-x-full"
            }`}
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-600 to-purple-800 text-white font-bold flex items-center justify-center text-[13px]">
                  LD
                </div>
                <span className="font-semibold text-gray-900 dark:text-white">
                  LarryDigitals
                </span>
              </div>
              <button onClick={closeSidebar} aria-label="Close menu">
                <XMarkIcon className="w-6 h-6 text-gray-900 dark:text-white" />
              </button>
            </div>

            <nav className="space-y-1 flex-1">
              {navItems.map(({ to, label, icon: Icon, external, badge, comingSoon }) => {
                if (comingSoon) {
                  return (
                    <div
                      key={label}
                      className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-gray-400 dark:text-gray-600 cursor-not-allowed select-none"
                    >
                      <Icon className="w-5 h-5 text-current flex-shrink-0" />
                      <span className="flex-1">{label}</span>
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 leading-none flex-shrink-0 uppercase">
                        Coming Soon
                      </span>
                    </div>
                  );
                }
                return (
                  <NavLink
                    key={label}
                    to={to}
                    end={to === "/dashboard"}
                    onClick={closeSidebar}
                    target={external ? "_blank" : undefined}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                        isActive
                          ? "bg-purple-600 text-white"
                          : "text-gray-800 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800"
                      }`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <Icon className="w-5 h-5 text-current flex-shrink-0" />
                        <span className="flex-1">{label}</span>
                        {badge && !isActive && (
                          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-amber-400 text-amber-900 leading-none flex-shrink-0">
                            {badge}
                          </span>
                        )}
                      </>
                    )}
                  </NavLink>
                );
              })}

              <button
                onClick={toggleTheme}
                className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-gray-800 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800 w-full text-left"
              >
                {darkMode ? (
                  <SunIcon className="w-5 h-5" />
                ) : (
                  <MoonIcon className="w-5 h-5" />
                )}
                {darkMode ? "Light mode" : "Dark mode"}
              </button>

              <button
                onClick={handleLogout}
                className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 w-full text-left"
              >
                <ArrowRightOnRectangleIcon className="w-5 h-5" />
                Logout
              </button>
            </nav>

            <div className="mt-6 rounded-2xl bg-[#111b21] border border-[#2a3942] p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-full bg-[#25D366] flex items-center justify-center shrink-0">
                    <WhatsAppIcon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-white leading-tight">
                      LarryDigitals
                    </p>
                    <p className="text-xs text-gray-400">Official updates</p>
                  </div>
                </div>
                <span className="flex items-center gap-1 text-[10px] font-semibold text-[#25D366] bg-[#25D366]/10 px-2 py-0.5 rounded-full">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#25D366] animate-pulse inline-block" />
                  LIVE
                </span>
              </div>

              <p className="text-xs text-gray-400 leading-relaxed mb-3">
                New logs &amp; numbers posted daily. Join to get notified the
                moment fresh stock drops.
              </p>

<a
                href="https://chat.whatsapp.com/J7ZTawEOmLw85ZFm9qOfbL"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl bg-[#25D366] hover:bg-[#20ba5a] active:bg-[#1aab52] transition-colors duration-150 text-white text-sm font-semibold"
              >
                <WhatsAppIcon className="w-4 h-4 text-white" />
                Join group
              </a>
            </div>
          </aside>
        </>
      )}

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-5 md:py-6 pb-24 md:pb-6">
        <Outlet />
      </main>

      {/* ── Mobile bottom nav ── */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/95 dark:bg-gray-900/95 backdrop-blur-md border-t border-gray-200 dark:border-gray-800 pb-[env(safe-area-inset-bottom)]">
        <div className="flex items-center justify-around h-16">
          {bottomNavItems.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={label}
              to={to}
              end={to === "/dashboard"}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-1 flex-1 h-full transition-colors ${
                  isActive
                    ? "text-purple-600 dark:text-purple-400"
                    : "text-gray-400 dark:text-gray-500"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <Icon className={`w-5 h-5 transition-transform ${isActive ? "scale-110" : ""}`} />
                  <span className="text-[10px] font-medium">{label}</span>
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}